// Formateadores de números para la UI, formato español (es-ES):
// separador de miles con punto y coma decimal. Spec diseño Inicio
// (ADR-025), sección 6 "Formato de datos".

/** Entero con separador de miles: 12345 → "12.345". */
export function fmtInt(n: number): string {
  return new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 }).format(n);
}

/** Número con 1 decimal y coma decimal: 58.2 → "58,2". */
export function fmt1(n: number): string {
  return new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(n);
}

/** Porcentaje con 1 decimal: 1.8 → "1,8 %". */
export function fmtPct(n: number): string {
  return `${fmt1(n)} %`;
}

/** Kilogramos → toneladas con 1 decimal: 58200 → "58,2 t". */
export function fmtT(kg: number): string {
  return `${fmt1(kg / 1000)} t`;
}

/** Litros → m³ con 1 decimal: 184600 → "184,6 m³". */
export function fmtM3(litros: number): string {
  return `${fmt1(litros / 1000)} m³`;
}

/** ppm con 1 decimal: 12.4 → "12,4 ppm". */
export function fmtPpm(ppm: number): string {
  return `${fmt1(ppm)} ppm`;
}

/** Grados enteros: 21 → "21°". */
export function fmtTemp(c: number): string {
  return `${fmtInt(Math.round(c))}°`;
}

/** Humedad entera: 62 → "62%". */
export function fmtHum(h: number): string {
  return `${fmtInt(Math.round(h))}%`;
}

/** Fecha corta en formato DD-MM-YYYY (español/europeo): "2026-08-25" → "25-08-2026".
 *  Acepta tanto `YYYY-MM-DD` (sin hora) como `YYYY-MM-DDTHH:MM:SS` (con hora)
 *  porque `new Date('2026-08-25')` se interpreta como UTC midnight y desplazaría
 *  la fecha un día en zonas horarias negativas. Para evitarlo, hacemos el
 *  parseo manual solo de la parte de fecha si viene sin "T".
 *  Regla de la app (2026-08-25): por defecto en todos los sitios. */
export function fmtFecha(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return '';
  return `${m[3]}-${m[2]}-${m[1]}`;
}

/** Fecha + hora en formato DD-MM-YYYY HH:MM (español/europeo):
 *  "2026-08-17T13:21:00Z" o "2026-08-17T13:21:00" → "17-08-2026 13:21".
 *  Para horas, sí usamos `new Date(iso)` porque `getHours()`/`getMinutes()`
 *  devuelven la hora LOCAL del usuario (que es lo que quiere ver). Si el
 *  backend manda hora UTC sin zona explícita (p.ej. "2026-08-17T13:21:00"
 *  sin "Z"), se interpreta como local — es el caso actual en este repo. */
export function fmtFechaHora(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const fecha = fmtFecha(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${fecha} ${hh}:${mm}`;
}

/** "Hace N min" / "Hace N h" / "Hace N días" / fecha+hora si más viejo.
 *  Si la fecha es hoy, devuelve "hace N min/h"; si es ayer, "ayer 13:21";
 *  si es esta semana, "Lun 13:21"; si es más viejo, "2026-08-15 13:21".
 *  Pensado para la confirmación temporal del parte del día en la
 *  Home (issue #9, auditoría 2026-08-24). */
export function fmtRelativeOrDate(iso: string, ahora: Date = new Date()): string {
  if (!iso) return '';
  let d: Date;
  try {
    d = new Date(iso);
  } catch {
    return '';
  }
  if (Number.isNaN(d.getTime())) return '';

  const diffMs = ahora.getTime() - d.getTime();
  const diffMin = Math.round(diffMs / 60_000);
  const diffH = Math.round(diffMs / 3_600_000);

  const sameDay =
    d.getFullYear() === ahora.getFullYear() &&
    d.getMonth() === ahora.getMonth() &&
    d.getDate() === ahora.getDate();
  if (sameDay) {
    if (diffMin < 1) return 'ahora mismo';
    if (diffMin < 60) return `hace ${diffMin} min`;
    return `hace ${diffH} h`;
  }

  const ayer = new Date(ahora);
  ayer.setDate(ayer.getDate() - 1);
  const isYesterday =
    d.getFullYear() === ayer.getFullYear() &&
    d.getMonth() === ayer.getMonth() &&
    d.getDate() === ayer.getDate();
  if (isYesterday) {
    return `ayer ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  const diffDays = Math.floor(diffMs / 86_400_000);
  if (diffDays < 7) {
    const wd = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'][d.getDay()];
    return `${wd} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  return fmtFechaHora(iso);
}
